import React, { useState } from 'react'
import axios from 'axios'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { FormPartInterface } from '../../../interfaces'
import { useClaimData } from '../../../claimProvider'
import { ButtonPrimary } from '../../reusable/buttonPrimary'
import { FilePlus2, FileText } from 'lucide-react'
import { Heading, HeadingMuted, Lead, FinePrint } from '../../ui/Text'
import styles from './styles.module.scss'

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.heic,.heif'
const MAX_BYTES = 15 * 1024 * 1024
const ALLOWED_TYPES = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/heic',
    'image/heif',
    'application/octet-stream',
]

type SlotId = 'poid' | 'utility_bill'

const SLOTS: { id: SlotId; label: string; hint: string }[] = [
    {
        id: 'poid',
        label: 'Photo ID',
        hint: 'Your passport, driving licence or national ID card. Make sure all four corners are in shot and the text is easy to read.',
    },
    {
        id: 'utility_bill',
        label: 'Utility bill',
        hint: 'A recent gas, electricity, water or council tax bill with your name and address on it.',
    },
]

const isAllowedFile = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    const allowedExt = ['pdf', 'png', 'jpg', 'jpeg', 'heic', 'heif']
    if (!allowedExt.includes(ext)) return 'Please upload a PDF, PNG, JPEG or HEIC file'
    if (file.size < 1) return 'The selected file is empty'
    if (file.size > MAX_BYTES) return 'File must be 15 MB or smaller'
    if (file.type && !ALLOWED_TYPES.includes(file.type.toLowerCase())) {
        return 'Please upload a PDF, PNG, JPEG or HEIC file'
    }
    return null
}

// A chosen photo shows as a thumbnail; anything the browser can't preview
// (PDF, HEIC) and the empty slot get a light line icon instead
const PREVIEWABLE = ['image/png', 'image/jpeg', 'image/jpg']

const SlotPreview = ({ file }: { file: File | null }) => {
    if (!file) return <FilePlus2 className={styles.slotIcon} aria-hidden="true" />
    if (!PREVIEWABLE.includes(file.type.toLowerCase())) {
        return <FileText className={styles.slotIcon} aria-hidden="true" />
    }
    return <img src={URL.createObjectURL(file)} alt="" />
}

export const UploadHearingLoss = (props: FormPartInterface) => {
    const { isActive, assignGroup } = props
    const claim = useClaimData()
    const firstName = claim?.clientName?.trim().split(/\s+/)[0] ?? ''
    const [files, setFiles] = useState<Record<SlotId, File | null>>({ poid: null, utility_bill: null })
    const [isSubmitting, setIsSubmitting] = useState(false)

    if (isActive !== 'upload') return <></>

    const bothReady = Boolean(files.poid && files.utility_bill)

    const onFileChange = (id: SlotId) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.files?.[0]
        if (!next) return
        const error = isAllowedFile(next)
        if (error) {
            Report.failure('File not accepted', error, 'OK')
            e.target.value = ''
            return
        }
        setFiles((prev) => ({ ...prev, [id]: next }))
    }

    const goComplete = () => assignGroup('complete')

    const submitUpload = async () => {
        if (!files.poid || !files.utility_bill) {
            Report.failure('Documents required', 'Please upload both a proof of ID and a utility bill.', 'OK')
            return
        }

        if (process.env.REACT_APP_MOCK_CLAIM === 'true') {
            goComplete()
            return
        }

        const endpoint = process.env.REACT_APP_API_HEARING_LOSS_UPLOAD
        if (!endpoint) {
            Report.failure('Upload unavailable', 'The upload service is not configured. Please try again later.', 'OK')
            return
        }

        setIsSubmitting(true)
        Loading.circle('Uploading documents')
        const body = new FormData()
        body.append('poid', files.poid)
        body.append('utility_bill', files.utility_bill)
        body.append('token', String(claim?.token || ''))
        body.append('case_ref', String(claim?.caseRef || ''))

        try {
            const response = await axios.post(endpoint, body, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            Loading.remove()
            setIsSubmitting(false)
            if (!response?.data?.success) {
                Report.failure(
                    'Upload failed',
                    response?.data?.message || 'We could not store your documents. Please try again.',
                    'OK'
                )
                return
            }
            goComplete()
        } catch (error) {
            console.log(error)
            Loading.remove()
            setIsSubmitting(false)
            Report.failure(
                'Upload failed',
                'We could not store your documents. Please try again.',
                'OK'
            )
        }
    }

    return (
        <div className={styles.container}>
            {/* Same conversational, scrambling-in heading as the claim form steps */}
            <Heading align="center" scrambleIn className="mf-excluded">
                <HeadingMuted>{firstName ? `Nearly there, ${firstName}.` : 'Nearly there.'}</HeadingMuted> Just two
                quick uploads.
            </Heading>
            <Lead align="center" className={styles.tagline}>
                Your claim documents have been signed and emailed to you. A photo of your ID and a recent utility bill
                help us confirm it&apos;s you and where you live.
            </Lead>

            <div className={styles.slots}>
                {SLOTS.map((slot) => (
                    <div key={slot.id} className={styles.slot}>
                        <p className={styles.slotLabel}>{slot.label}</p>
                        <p className={styles.slotHint}>{slot.hint}</p>
                        <div className={styles.dropzone}>
                            <input
                                id={`mhl-${slot.id}`}
                                type="file"
                                accept={ACCEPT}
                                onChange={onFileChange(slot.id)}
                                className={styles.fileInput}
                            />
                            <label htmlFor={`mhl-${slot.id}`} className={styles.fileLabel}>
                                <SlotPreview file={files[slot.id]} />
                                <span>{files[slot.id] ? files[slot.id]!.name : 'Click to select a file'}</span>
                            </label>
                        </div>
                    </div>
                ))}
            </div>
            <FinePrint align="center" className={styles.formats}>
                PDF, PNG, JPEG or HEIC, up to 15 MB each.
            </FinePrint>

            <div className={styles.actions}>
                <ButtonPrimary onClick={submitUpload} loading={isSubmitting} disabled={!bothReady || isSubmitting}>
                    Upload and continue
                </ButtonPrimary>
            </div>
        </div>
    )
}
